import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { FiEdit } from "react-icons/fi";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { MdDelete } from "react-icons/md";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { checkAcessRights, formatIndianNumber } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
  PRINT_ACCESS,
} from "../../_contstants/common";
import {
  deleteCreditnoteVoucher,
  getAllCreditnoteVoucher,
} from "./services/creditnoteVoucher";
import { setCreditnoteVoucherGUID } from "./creditnoteVoucherSlice";
import { FaPrint } from "react-icons/fa";

const CreditnoteVoucher: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    return () => {
      dispatch(setCurrentPage(0));
      dispatch(closeDialog());
    };
  }, []);

  const handleCreate = () => {
    navigate("/creditnotevoucher/create");
  };

  return (
    <>
      <DeleteCreditnoteVoucherDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Credit Note Voucher List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              {"Add New"}
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <CreditnoteVoucherGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const CreditnoteVoucherGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const isEditBtnRef = useRef<any>(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const isPrintAccess = checkAcessRights(location.pathname, PRINT_ACCESS);
  const CreditnoteVoucherList = useAppSelector(
    (state) => state.creditnotevoucher.CreditnoteVoucherList
  );
  const loading = useAppSelector((state) => state.creditnotevoucher.loading);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  useEffect(() => {
    const payload = {
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    dispatch(getAllCreditnoteVoucher(payload));
  }, [currentPage, pageLimit]);
  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEdit = (credit_note_voucher_guid: any) => {
      navigate("/creditnotevoucher/edit", {
        state: { credit_note_voucher_guid: credit_note_voucher_guid },
      });
    };

    const handleOpenDeleteDialog = (credit_note_voucher_guid: any) => {
      dispatch(openDialog("deleteCreditnoteVoucher"));
      dispatch(setCreditnoteVoucherGUID(credit_note_voucher_guid));
    };

    const handlePrintCreditNoteVoucher = (credit_note_voucher_guid: string) => {
      window.open(`/creditnotevoucherprint/${credit_note_voucher_guid}`);
    };

    return (
      <td
        className={`action-td ${props.className}`}
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            ref={isEditBtnRef}
            onClick={() => {
              isEditBtnRef.current = true;
              handleEdit(props.dataItem?.credit_note_voucher_guid);
            }}
          >
            <FiEdit
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            onClick={() =>
              handleOpenDeleteDialog(props.dataItem?.credit_note_voucher_guid)
            }
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}
        {isPrintAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Print"
            onClick={() =>
              handlePrintCreditNoteVoucher(
                props.dataItem?.credit_note_voucher_guid
              )
            }
          >
            <FaPrint
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 6 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        style={{ maxHeight: "calc(100vh - 152px)" }}
        className="responsive-table"
        filterable={true}
        filter={filter}
        data={
          filter
            ? filterBy(CreditnoteVoucherList, filter)
            : CreditnoteVoucherList
        }
        onFilterChange={handleFilterChange}
        total={
          CreditnoteVoucherList[0]?.itemcount || CreditnoteVoucherList?.length
        }
        skip={currentPage * pageLimit}
        take={pageLimit}
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 50, 100, 300, 500],
          pageSizeValue: pageLimit,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          field="credit_note_voucher_no_string"
          title="Credit Note No"
          width={150}
          cell={(props: any) => (
            <td>
              <div
                className={isEditAccess ? "hoverableName" : "normalName"}
                onClick={() =>
                  isEditAccess &&
                  navigate("/creditnotevoucher/edit", {
                    state: {
                      credit_note_voucher_guid:
                        props.dataItem?.credit_note_voucher_guid,
                    },
                  })
                }
              >
                {props.dataItem?.credit_note_voucher_no_string}
              </div>
            </td>
          )}
        />
        <Column
          field="voucher_type"
          title="Voucher Type"
          filterable={false}
          width={130}
        />
        <Column
          field="credit_note_voucher_date"
          title="Credit Note  Date"
          filterable={false}
          width={150}
        />
        <Column
          field="ledger_name_entry"
          title="Ledger Name"
          filterable={false}
          width={150}
        />
        <Column
          field="credit_note_voucher_total_amount"
          title="Credit Note Amount"
          width={180}
          filterable={false}
          cell={(props: any) => (
            <td className="text-end">
              {props.dataItem?.credit_note_voucher_total_amount
                ? formatIndianNumber(
                    props.dataItem?.credit_note_voucher_total_amount
                  )
                : 0}
            </td>
          )}
        />
        <Column field="remarks" title="Remarks" width={150} />
        {(isEditAccess || isDeleteAccess || isPrintAccess) && (
          <Column
            field="id"
            title="Actions"
            cell={MyEditCommandCell}
            width={"140px"}
            locked={true}
            filterable={false}
          />
        )}
      </KendoGrid>
    </>
  );
};

const DeleteCreditnoteVoucherDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const CreditnoteVoucherGUID = useAppSelector(
    (state) => state.creditnotevoucher.CreditnoteVoucherGUID
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const handleDeleteAction = async (ID: any) => {
    if (ID) {
      const payload = {
        limit: +pageLimit,
        skip: +currentPage * +pageLimit,
      };
      const response = await dispatch(deleteCreditnoteVoucher(ID));
      if (response.payload.Data.Status === 200) {
        dispatch(getAllCreditnoteVoucher(payload));
        dispatch(closeDialog());
        dispatch(setCreditnoteVoucherGUID(""));
      } else {
        dispatch(getAllCreditnoteVoucher(payload));
        dispatch(closeDialog());
        dispatch(setCreditnoteVoucherGUID(""));
      }
    }
  };
  return (
    <>
      {dialogName === "deleteCreditnoteVoucher" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Credit Note Voucher"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Credit Note Voucher?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(CreditnoteVoucherGUID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default CreditnoteVoucher;
